import { useState } from "react";
import { Link, Redirect, useLocalSearchParams } from "expo-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, FormTitle, InputWithLabel, Layout, Logo } from "@ptsd/uikit";
import { ArrowLeft } from "@tamagui/lucide-icons";
import i18next from "i18next";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Form, Image, useMedia, View, XStack } from "tamagui";
import { z } from "zod";

import { api } from "~/utils/api";
import { authClient } from "~/utils/auth";
import { Footer, Header } from "~/components/layout";

//TODO: update error messages coz we have one error for that cover several different conditions
const schema = z
  .object({
    password: z
      .string()
      .min(8, {
        message: i18next.t("authValidationMessages:password.requirements"),
      })
      .max(32, {
        message: i18next.t("authValidationMessages:password.requirements"),
      })
      .refine((password) => /[A-Z]/.test(password), {
        message: i18next.t("authValidationMessages:password.requirements"),
      })
      .refine((password) => /[a-z]/.test(password), {
        message: i18next.t("authValidationMessages:password.requirements"),
      }),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: i18next.t("authValidationMessages:password.password-match"),
    path: ["confirmPassword"],
  });

export type FormData = z.infer<typeof schema>;

//eslint-disable-next-line @typescript-eslint/no-var-requires
const img = require("../../assets/signup/signup-3.png");

//TODO: update i18n

const ResetPassword = () => {
  const { sm } = useMedia();

  return (
    <Layout variant="dualbackground">
      {sm && <Header />}
      <View
        $sm={{
          flex: 1,
        }}
        flex={2}
        flexDirection="row"
        width={"100%"}
      >
        <View
          paddingHorizontal={"$2.5"}
          flex={1}
          alignItems="flex-end"
          width={"50%"}
          $gtSm={{
            paddingRight: 110,
          }}
          $sm={{
            alignItems: "center",
          }}
        >
          {!sm && (
            <View width={"100%"} paddingTop={"$5.8"} maxWidth={413}>
              <Logo
                style={{
                  width: 214,
                  height: 36,
                }}
              />
            </View>
          )}

          <ResetPasswordFrom />
        </View>
        {!sm && (
          <View
            paddingHorizontal={"$2.5"}
            width={"50%"}
            alignItems="flex-start"
            paddingLeft={110}
            paddingTop={200}
            justifyContent="flex-start"
            flex={1}
          >
            <Image
              maxHeight={560}
              maxWidth={600}
              src={img}
              contain="size"
              width="100%"
              resizeMode="contain"
              aspectRatio={1}
            />
          </View>
        )}
      </View>
      {sm && <Footer />}
    </Layout>
  );
};

export default ResetPassword;

const ResetPasswordFrom = () => {
  const { sm } = useMedia();
  const { token } = useLocalSearchParams<{ token: string }>();

  const { t } = useTranslation("resetpassword");

  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const [isAuthorized] = api.auth.isAuthorized.useSuspenseQuery(undefined);

  const { setError, control, handleSubmit } = useForm<FormData>({
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
    resolver: zodResolver(schema),
  });

  const onSubmit = (data: z.infer<typeof schema>) => {
    if (isLoading || isSuccess) {
      return;
    }
    void (async () => {
      setIsSuccess(false);
      setIsLoading(true);
      const result = await authClient.resetPassword({
        newPassword: data.password,
        token,
      });
      if (result.error?.code) {
        setError("password", {
          message: t("unknown-error"),
        });
      }
      if (result.data?.status === true) {
        setIsSuccess(true);
      }
      setIsLoading(false);
    })();
  };

  if (isAuthorized || !token) {
    return <Redirect href={"/"} />;
  }

  return (
    <Form
      width={"100%"}
      maxWidth={413}
      gap={"$3"}
      paddingVertical={"$15"}
      $sm={{
        paddingVertical: "$4",
      }}
      flex={1}
      justifyContent="flex-start"
      onSubmit={handleSubmit(onSubmit)}
    >
      <View width={"100%"} gap={"$5"}>
        {isSuccess && (
          <View gap={"$3"}>
            <View gap={"$2"}>
              <FormTitle>{t("success-message")}</FormTitle>
            </View>
          </View>
        )}
        {!isSuccess && (
          <>
            <FormTitle>{t("form.title")}</FormTitle>

            <View width={"100%"} gap={"$2"}>
              <View width={"100%"} gap={"$4"}>
                <Controller
                  control={control}
                  name={"password"}
                  rules={{
                    required: true,
                  }}
                  render={({ field, fieldState }) => (
                    <InputWithLabel
                      data-id={"reset-password-input"}
                      secureTextEntry
                      variant="inline"
                      size="md"
                      isError={!!fieldState.error?.message}
                      error={fieldState.error?.message}
                      isMessage
                      message={t("form.password.message")}
                      label={t("form.password.label")}
                      placeholder={t("form.password.placeholder")}
                      onChangeText={(text) => field.onChange(text)}
                      {...field}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name={"confirmPassword"}
                  rules={{
                    required: true,
                  }}
                  render={({ field, fieldState }) => (
                    <InputWithLabel
                      data-id={"reset-password-confirm-input"}
                      secureTextEntry
                      variant="inline"
                      size="md"
                      isMessage
                      message={t("form.confirmPassword.message")}
                      isError={!!fieldState.error?.message}
                      error={fieldState.error?.message}
                      label={t("form.confirmPassword.label")}
                      placeholder={t("form.confirmPassword.placeholder")}
                      onChangeText={(text) => field.onChange(text)}
                      {...field}
                    />
                  )}
                />
              </View>
            </View>
          </>
        )}
      </View>

      <XStack gap={"$0.5"}>
        <Link data-id={"reset-password-back"} href="/auth" asChild>
          <Button
            variant="gray"
            size={sm ? "md" : "lg"}
            mode="icon"
            rounded="lg"
          >
            <ArrowLeft color={"$gray-primary-900"} size={"$2.5"} />
          </Button>
        </Link>

        {!isSuccess && (
          <Form.Trigger asChild>
            <Button
              data-id={"reset-password-submit"}
              flex={1}
              variant="primary"
              size={sm ? "md" : "lg"}
              rounded="lg"
            >
              {isLoading ? t("form.submit.loading") : t("form.submit.label")}
            </Button>
          </Form.Trigger>
        )}
      </XStack>
    </Form>
  );
};
